import { Injectable, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { switchMap, takeUntil, tap } from 'rxjs/operators';
import { BehaviorSubject, Observable } from 'rxjs';

import { ModalsService } from '@core/services/modals/modals.service';
import { EntitiesService } from '@core/services/entities/entities.service';
import { OrderTypeService } from '@core/services/order-type/order-type.service';
import { AnalyticsService } from '@core/services/app/analytics.service';

import { DoodWantedAtType } from '@store/settings/settings.model';
import { SettingsStoreSelector } from '@common/selectors/settings.selector';
import { ParametersDistributionModeValues } from '@config/values/parameters.values';
import { ShopSearchParametersModalComponent } from '@shared/modals/shop-search-parameters-modal/shop-search-parameters-modal.component';

import {
  SHOP_AROUND_LIST_BODY_BLOCK_LOADING_VM,
  ShopAroundListBodyBlockDataSource,
  ShopAroundListBodyBlockViewModel,
} from './shop-around-list-body-block-view-model';

@Injectable()
export class ShopAroundListBodyBlockStoreDataSource
  implements ShopAroundListBodyBlockDataSource, OnDestroy
{
  private readonly destroyed$ = new Subject<boolean>();
  private readonly vmSubject = new BehaviorSubject<ShopAroundListBodyBlockViewModel>(
    SHOP_AROUND_LIST_BODY_BLOCK_LOADING_VM,
  );

  readonly vm$: Observable<ShopAroundListBodyBlockViewModel> = this.vmSubject.asObservable();

  constructor(
    private readonly modalsService: ModalsService,
    private readonly settingsSelector: SettingsStoreSelector,
    private readonly entitiesService: EntitiesService,
    private readonly orderTypeService: OrderTypeService,
    private readonly analyticsService: AnalyticsService,
  ) {
    this.settingsSelector.selectParameters$
      .pipe(
        tap(parameters => {
          const { location, distribution_mode, wanted_at, wanted_at_type } = parameters;
          const orderTypeCapabilities = this.orderTypeService.getCapabilities(distribution_mode);
          const isLocationMandatory =
            !location && distribution_mode === ParametersDistributionModeValues.Delivery;

          if (
            isLocationMandatory ||
            (isLocationMandatory && !wanted_at_type) ||
            (orderTypeCapabilities.preorderingAllowed &&
              wanted_at_type === DoodWantedAtType.later &&
              !wanted_at)
          ) {
            this.modalsService.open(ShopSearchParametersModalComponent.handle);
          }

          this.vmSubject.next({
            ...this.vmSubject.value,
            wantedAt: wanted_at,
          });
        }),
        switchMap(() => {
          const parameters = this.settingsSelector.parameters;
          this.analyticsService.trackEvent('search_shops', {
            search_shops_location: parameters.location,
            search_shops_wanted_at: parameters.wanted_at,
            search_shops_distribution_mode: parameters.distribution_mode,
          });
          return this.entitiesService.loadProximityEntities$();
        }),
        takeUntil(this.destroyed$),
        tap(entities => {
          const parameters = this.settingsSelector.parameters;

          if (entities.length === 0) {
            this.analyticsService.trackEvent('search_shops_not_found', {
              search_shops_location: parameters.location,
              search_shops_wanted_at: parameters.wanted_at,
              search_shops_distribution_mode: parameters.distribution_mode,
            });
          } else {
            this.analyticsService.trackEvent('search_shops_success', {
              search_shops_result_count: entities.length,
              search_shops_location: parameters.location,
              search_shops_wanted_at: parameters.wanted_at,
              search_shops_distribution_mode: parameters.distribution_mode,
            });
          }

          this.vmSubject.next({
            ...this.vmSubject.value,
            shops: entities,
          });
        }),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
